import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  Fab,
  Badge,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ShoppingCart as CartIcon,
  LocalBar as WineIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '../store/useStore';
import { firebaseWinesService, firebaseCategoriesService } from '../services/firebaseService';
import { mockWinesApi, mockCategoriesApi } from '../services/mockData';
import { wineriesService } from '../services/dataService';
import { firebaseSectionService } from '../services/firebaseSectionService';
import useMediaQuery from '@mui/material/useMediaQuery';
import SectionDivider from '../components/common/SectionDivider';

// Componente placeholder mejorado para cuando no hay imagen
const WinePlaceholder = ({ name }) => (
  <Box
    sx={{
      height: 280,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      border: '1px solid #e0e0e0',
      borderRadius: 2,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle, rgba(114, 47, 55, 0.05) 0%, transparent 70%)',
        animation: 'pulse 3s ease-in-out infinite'
      }
    }}
  >
    <WineIcon sx={{ 
      fontSize: 80, 
      color: '#722F37', 
      mb: 2,
      opacity: 0.7,
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
    }} />
    <Typography 
      variant="caption" 
      color="text.secondary" 
      textAlign="center" 
      sx={{ 
        px: 2,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: 1
      }}
    >
      {name.length > 20 ? `${name.substring(0, 20)}...` : name}
    </Typography>
  </Box>
);

// Componente Hero Section
const HeroSection = () => (
  <Box
    sx={{
      backgroundColor: '#fff',
      backgroundImage: `url('/images/logo-circular.png')`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center top',
      backgroundSize: { xs: 'min(78vw, 260px)', md: 'min(360px, 52%)' },
      color: '#722F37',
      pt: { xs: 1.5, md: 3 },
      pb: { xs: 2.5, md: 4 },
      px: 2,
      mb: { xs: 2, md: 6 },
      borderRadius: 3,
      minHeight: { xs: 320, md: 420 },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    }}
  >
    <Container maxWidth="md">
      <Box textAlign="center" sx={{ pt: { xs: 32, md: 44 } }}>
        <Typography
          variant="body1"
          sx={{
            maxWidth: 620,
            mx: 'auto',
            lineHeight: 1.6,
            color: '#722F37',
            fontSize: { xs: '0.95rem', md: '1.15rem' },
            fontWeight: 500
          }}
        >
          “El vino da brillantez a las campiñas, exalta los corazones, enciende las pupilas y enseña a los pies la danza.”
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 1.5,
            fontStyle: 'italic',
            color: '#722F37',
            fontSize: { xs: '0.9rem', md: '1rem' }
          }}
        >
          José Ortega y Gasset.
        </Typography>
      </Box>
    </Container>
  </Box>
);

// Componente de filtros mejorado
const FilterSection = ({ filters, handleFilterChange, categories, wineTypes, wineries, onSearch }) => (
  <Paper 
    elevation={0} 
    sx={{ 
      p: 3, 
      mb: 4, 
      border: '1px solid #e0e0e0',
      borderRadius: 3,
      background: 'rgba(248, 249, 250, 0.7)',
      backdropFilter: 'blur(10px)'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
      <FilterIcon sx={{ mr: 1, color: 'secondary.main' }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
        Filtrar Vinos
      </Typography>
    </Box>
    <Grid container spacing={3} alignItems="center" justifyContent="center">
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          placeholder="Buscar vinos..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              onSearch();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  onClick={onSearch}
                  sx={{ 
                    minWidth: 'auto',
                    px: 2,
                    py: 0.5,
                    backgroundColor: 'secondary.main',
                    color: 'white',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'secondary.dark'
                    }
                  }}
                >
                  Buscar
                </Button>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'secondary.main'
                }
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'secondary.main'
                }
              }
            }
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          select
          label="Denominaciones de origen"
          value={filters.categoryId}
          onChange={(e) => handleFilterChange('categoryId', e.target.value)}
          sx={{
            minWidth: 200,
            maxWidth: 300,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              minWidth: 160,
              maxWidth: 300
            }
          }}
        >
          <MenuItem value="">Todas</MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={2} sx={{ display: { xs: 'none', md: 'block' } }}>
        <TextField
          fullWidth
          select
          label="Bodega"
          value={filters.wineryId}
          onChange={(e) => handleFilterChange('wineryId', e.target.value)}
          sx={{
            minWidth: 120,
            maxWidth: 180,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              minWidth: 100,
              maxWidth: 180
            }
          }}
        >
          <MenuItem value="">Todas</MenuItem>
          {wineries.map((winery) => (
            <MenuItem key={winery.id} value={winery.id}>
              {winery.name}{winery.region ? ` (${winery.region})` : ''}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
        <TextField
          fullWidth
          select
          label="Tipo"
          value={filters.type}
          onChange={(e) => handleFilterChange('type', e.target.value)}
          sx={{
            minWidth: 120,
            maxWidth: 180,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              minWidth: 100,
              maxWidth: 180
            }
          }}
        >
          <MenuItem value="">Todos</MenuItem>
          {Array.isArray(wineTypes) && wineTypes.filter(Boolean).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {/* Agrupación móvil: Bodega y Tipo en la misma fila */}
      <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Bodega"
              value={filters.wineryId}
              onChange={(e) => handleFilterChange('wineryId', e.target.value)}
              sx={{
                minWidth: 100,
                maxWidth: 180,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                  minWidth: 100,
                  maxWidth: 180
                }
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {wineries.map((winery) => (
                <MenuItem key={winery.id} value={winery.id}>
                  {winery.name}{winery.region ? ` (${winery.region})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              select
              label="Tipo"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              sx={{
                minWidth: 100,
                maxWidth: 180,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                  minWidth: 100,
                  maxWidth: 180
                }
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Array.isArray(wineTypes) && wineTypes.filter(Boolean).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Grid>
      {/* Eliminado el picker de ordenar y el botón de orden ascendente/descendente */}
    </Grid>
  </Paper>
);

// Componente de carrusel horizontal para productos
const ProductCarousel = ({ title, products, onAddToCart, imageErrors, handleImageError, formatPrice, setQuickViewWine, setQuickViewOpen }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const itemsPerView = isMobile ? 2 : 4;
  const [currentIndex, setCurrentIndex] = useState(0);
  const maxIndex = Math.max(0, products.length - itemsPerView);

  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };

  const visibleProducts = products
    .filter(wine => wine.stock > 0)
    .slice(currentIndex, currentIndex + itemsPerView);

  // Cálculo de páginas
  const totalPages = Math.ceil(products.length / itemsPerView);
  let currentPage = Math.floor(currentIndex / itemsPerView);
  if (currentIndex >= (totalPages - 1) * itemsPerView) {
    currentPage = totalPages - 1;
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Controles de navegación */}
      {currentIndex > 0 && (
        <IconButton
          onClick={handlePrev}
          sx={{
            position: 'absolute',
            left: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              transform: 'translateY(-50%) scale(1.1)'
            }
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      
      {currentIndex < maxIndex && (
        <IconButton
          onClick={handleNext}
          sx={{
            position: 'absolute',
            right: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              transform: 'translateY(-50%) scale(1.1)'
            }
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {/* Contenedor del carrusel */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1.5, md: 3 },
          overflow: 'hidden',
          px: 1,
          transition: 'all 0.3s ease',
          alignItems: 'stretch',
          height: '100%',
          minHeight: 420,
        }}
      >
        {visibleProducts.map((wine) => (
          <Card 
            key={wine.id}
            sx={{ 
              minWidth: isMobile ? '37vw' : undefined,
              maxWidth: isMobile ? '37vw' : undefined,
              flex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              border: '1px solid #e0e0e0',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(114, 47, 55, 0.15)',
                borderColor: '#722F37',
                '& .wine-image': {
                  transform: 'scale(1.05)'
                }
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #722F37 0%, #8B4513 100%)',
                transform: 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.3s ease'
              },
              '&:hover::before': {
                transform: 'scaleX(1)'
              },
              // Reducir padding vertical en móvil
              ...(isMobile && {
                mb: 1.5,
              })
            }}
          >
            {/* Imagen del vino o placeholder */}
            {wine.imageUrl && !imageErrors.has(wine.id) ? (
              <Box
                sx={{
                  width: '100%',
                  height: 160,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                  borderRadius: 2,
                  border: '1px solid #eee',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  p: 1,
                  overflow: 'hidden'
                }}
              >
                <img
                  src={wine.imageUrl}
                  alt={wine.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                  onError={() => handleImageError(wine.id)}
                  onClick={() => {
                    setQuickViewWine(wine);
                    setQuickViewOpen(true);
                  }}
                />
              </Box>
            ) : (
              <WinePlaceholder name={wine.name} />
            )}
            
            <CardContent sx={{ flex: 1, p: isMobile ? 1.5 : 3, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: 0 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  mb: isMobile ? 1 : 2,
                  fontSize: '1.1rem',
                  lineHeight: 1.3,
                  color: '#2c2c2c'
                }}
              >
                {wine.name}
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mb: isMobile ? 1 : 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip 
                  label={wine.type} 
                  size="small" 
                  sx={{
                    backgroundColor: '#722F37',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#8B4513'
                    }
                  }}
                />
                {wine.year && (
                  <Chip 
                    label={wine.year} 
                    size="small" 
                    variant="outlined"
                    sx={{
                      borderColor: '#722F37',
                      color: '#722F37',
                      fontWeight: 600
                    }}
                  />
                )}
                {wine.alcoholContent && (
                  <Chip 
                    label={`${wine.alcoholContent}% Vol.`} 
                    size="small" 
                    variant="outlined"
                    sx={{
                      borderColor: '#8B4513',
                      color: '#8B4513',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Stack>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  mb: isMobile ? 1.5 : 3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.5
                }}
              >
                {wine.description}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isMobile ? 1 : 2 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#722F37',
                    fontSize: '1.5rem'
                  }}
                >
                  {formatPrice(wine.price)}
                </Typography>
              </Box>
            </CardContent>
            
            <CardActions sx={{ p: isMobile ? 1.5 : 3, pt: 0, mt: 'auto' }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => onAddToCart(wine)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#722F37',
                  '&:hover': {
                    backgroundColor: '#8B4513',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(114, 47, 55, 0.3)'
                  }
                }}
              >
                Añadir al Carrito
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Eliminados los indicadores de navegación */}
    </Box>
  );
};

const QuickViewModal = ({ open, wine, onClose, onAddToCart, imageErrors, handleImageError, formatPrice }) => {
  if (!wine) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Roboto, Arial, sans-serif', color: '#722F37', pr: 5 }}>
        {wine.name}
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        {wine.imageUrl && !imageErrors.has(wine.id) ? (
          <img
            src={wine.imageUrl}
            alt={wine.name}
            style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 12, marginBottom: 24 }}
            onError={() => handleImageError(wine.id)}
          />
        ) : (
          <WinePlaceholder name={wine.name} />
        )}
        <Typography variant="body1" sx={{ mt: 2, mb: 3, color: '#333', fontSize: '1.1rem' }}>
          {wine.description}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#722F37', mb: 2 }}>
          {formatPrice(wine.price)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => onAddToCart(wine)}
          sx={{
            borderRadius: 2,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            backgroundColor: '#722F37',
            '&:hover': {
              backgroundColor: '#8B4513',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(114, 47, 55, 0.3)'
            }
          }}
        >
          Añadir al Carrito
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { addToCart, cart } = useStore();
  const [filters, setFilters] = useState({
    search: '',
    categoryId: '',
    wineryId: '',
    type: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });
  const [activeSearch, setActiveSearch] = useState('');
  const [alert, setAlert] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [quickViewWine, setQuickViewWine] = useState(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  // Crear filtros con búsqueda activa (solo cuando se presiona el botón)
  const queryFilters = useMemo(() => ({
    ...filters,
    search: activeSearch
  }), [filters, activeSearch]);

  // Función para manejar la búsqueda manual
  const handleSearch = () => {
    setActiveSearch(filters.search);
  };

  // Función para manejar cambios en filtros (excepto búsqueda)
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Si es un filtro que no sea búsqueda, aplicar inmediatamente
    if (field !== 'search') {
      setActiveSearch(filters.search);
    }
  };

  // Hook de consulta de vinos (esto debe ir ANTES del useMemo)
  const { data: winesData, isLoading: winesLoading, error: winesError } = useQuery({
    queryKey: ['firebaseWines', queryFilters],
    queryFn: async () => {
      try {
        console.log('🍷 Home: Consultando vinos desde Firebase...');
        const response = await firebaseWinesService.getAll(queryFilters);
        console.log('✅ Vinos obtenidos de Firebase para Home:', response.data.data.length);
        return { data: response.data.data };
      } catch (error) {
        console.warn('⚠️ Error con Firebase en Home, usando API tradicional...', error);
        try {
          const response = await firebaseWinesService.getAll(queryFilters);
          return response.data;
        } catch (fallbackError) {
          console.warn('⚠️ Error con API tradicional en Home, usando datos mock...', fallbackError);
          const response = await mockWinesApi.getAll();
          return { data: response.data.data };
        }
      }
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // useMemo para wines, con fallbackWines dentro
  const wines = useMemo(() => {
  const fallbackWines = [
    {
      id: '1',
      name: 'Marqués de Riscal Reserva',
      description: 'Un vino tinto elegante con notas de frutas maduras y especias.',
      price: 25.99,
      imageUrl: '/images/wines/marques-riscal.jpg',
      categoryId: '1',
      category: { id: '1', name: 'Rioja' },
      type: 'Tinto',
      year: 2018,
      alcoholContent: 13.5,
      stock: 50
    },
    {
      id: '2',
      name: 'Vega Sicilia Único',
      description: 'Uno de los vinos más prestigiosos de España, con gran complejidad.',
      price: 89.99,
      imageUrl: '/images/wines/vega-sicilia.jpg',
      categoryId: '2',
      category: { id: '2', name: 'Ribera del Duero' },
      type: 'Tinto',
      year: 2015,
      alcoholContent: 14.0,
      stock: 25
    },
    {
      id: '3',
      name: 'Albariño Rías Baixas',
      description: 'Vino blanco fresco y aromático de las costas gallegas.',
      price: 18.50,
      imageUrl: '/images/wines/albarino.jpg',
      categoryId: '3',
      category: { id: '3', name: 'Rías Baixas' },
      type: 'Blanco',
      year: 2022,
      alcoholContent: 12.5,
      stock: 75
    },
    {
      id: '4',
      name: 'Cava Codorníu',
      description: 'Espumoso tradicional con burbujas finas y persistentes.',
      price: 12.99,
      imageUrl: '/images/wines/codorniu.jpg',
      categoryId: '4',
      category: { id: '4', name: 'Cava' },
      type: 'Espumoso',
      year: 2021,
      alcoholContent: 11.5,
      stock: 100
    },
    {
      id: '5',
      name: 'Jumilla Monastrell',
      description: 'Vino tinto potente de la región de Murcia.',
      price: 15.75,
      imageUrl: '/images/wines/jumilla.jpg',
      categoryId: '5',
      category: { id: '5', name: 'Jumilla' },
      type: 'Tinto',
      year: 2020,
      alcoholContent: 14.5,
      stock: 60
    }
  ];
    return Array.isArray(winesData?.data) ? winesData.data : (winesError ? fallbackWines : []);
  }, [winesData, winesError]);

  // Obtener categorías usando Firebase como fuente principal
  const { data: categoriesData } = useQuery({
    queryKey: ['firebaseCategories'],
    queryFn: async () => {
      try {
        console.log('📂 Home: Consultando categorías desde Firebase...');
        const response = await firebaseCategoriesService.getAll();
        console.log('✅ Categorías obtenidas de Firebase para Home:', response.data.data.length);
        return { data: response.data.data };
      } catch (error) {
        console.warn('⚠️ Error con Firebase categorías en Home, usando API tradicional...', error);
        try {
          const response = await firebaseCategoriesService.getAll();
          return response.data;
        } catch (fallbackError) {
          console.warn('⚠️ Error con API tradicional categorías en Home, usando datos mock...', fallbackError);
          const response = await mockCategoriesApi.getAll();
          return { data: response.data.data };
        }
      }
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Filtrar categorías visibles
  const visibleCategories = Array.isArray(categoriesData?.data)
    ? categoriesData.data.filter(cat => cat.visible === true)
    : [
        { id: '1', name: 'Rioja' },
        { id: '2', name: 'Ribera del Duero' },
        { id: '3', name: 'Rías Baixas' },
        { id: '4', name: 'Cava' },
        { id: '5', name: 'Jumilla' }
      ];

  // Justo después de calcular visibleCategories
  console.log('[DEBUG] Categorías recibidas:', categoriesData?.data);
  console.log('[DEBUG] Categorías visibles:', visibleCategories.map(cat => ({ id: cat.id, name: cat.name, visible: cat.visible })));

  // Obtener bodegas usando useQuery
  const { data: wineriesData = [] } = useQuery({
    queryKey: ['wineries'],
    queryFn: async () => {
      try {
        const response = await wineriesService.getAll();
        return response;
      } catch (error) {
        console.warn('⚠️ Error obteniendo bodegas:', error);
        return [];
      }
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Filtrar bodegas según denominación seleccionada y ordenar alfabéticamente
  const filteredWineries = useMemo(() => {
    if (!Array.isArray(wineriesData)) return [];
    if (!filters.categoryId) return wineriesData.sort((a, b) => a.name.localeCompare(b.name));
    // Obtener los wineryId de los vinos que pertenecen a la denominación seleccionada
    const wineryIdsWithCategory = new Set(
      wines.filter(w => w.categoryId === filters.categoryId).map(w => w.wineryId)
    );
    return wineriesData.filter(winery => wineryIdsWithCategory.has(winery.id))
                      .sort((a, b) => a.name.localeCompare(b.name));
  }, [wineriesData, wines, filters.categoryId]);

  // Filtrar bodegas visibles y ordenar alfabéticamente
  const visibleWineries = Array.isArray(wineriesData)
    ? wineriesData.filter(w => w.visible === true).sort((a, b) => a.name.localeCompare(b.name))
    : [
        { id: '1', name: 'Marqués de Riscal', region: 'Rioja' },
        { id: '2', name: 'Vega Sicilia', region: 'Ribera del Duero' },
        { id: '3', name: 'Albarino', region: 'Rías Baixas' },
        { id: '4', name: 'Codorníu', region: 'Cava' },
        { id: '5', name: 'Jumilla', region: 'Murcia' }
      ].sort((a, b) => a.name.localeCompare(b.name));

  // Justo después de calcular visibleWineries
  console.log('[DEBUG] Bodegas recibidas:', wineriesData);
  console.log('[DEBUG] Bodegas visibles:', visibleWineries.map(w => ({ id: w.id, name: w.name, visible: w.visible })));

  // Obtener secciones personalizadas activas
  const { data: sectionsData } = useQuery({
    queryKey: ['firebaseSections'],
    queryFn: async () => {
      try {
        const sections = await firebaseSectionService.getActiveSections();
        return sections;
      } catch (error) {
        console.warn('⚠️ Error obteniendo secciones personalizadas en Home:', error);
        return [];
      }
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Filtrar secciones personalizadas visibles
  const visibleSections = Array.isArray(sectionsData)
    ? sectionsData.filter(s => s.visible !== false && s.isActive && Array.isArray(s.wineIds))
    : [];

  // Usar datos de Firebase si están disponibles, sino usar fallback
  const categories = Array.isArray(categoriesData?.data) 
    ? categoriesData.data 
    : [
        { id: '1', name: 'Rioja' },
        { id: '2', name: 'Ribera del Duero' },
        { id: '3', name: 'Rías Baixas' },
        { id: '4', name: 'Cava' },
        { id: '5', name: 'Jumilla' }
      ];

  // Función para obtener el nombre de la categoría
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Sin categoría';
  };
  
  const wineTypes = useMemo(() => {
    if (!Array.isArray(wines)) return [];
    const typesSet = new Set();
    wines.forEach(wine => {
      if (wine.type) typesSet.add(wine.type);
    });
    return Array.from(typesSet);
  }, [wines]);

  // Filtrar vinos basado en los filtros aplicados
  const filteredWines = useMemo(() => {
    if (!Array.isArray(wines)) return [];
    return wines.filter(wine => {
      // Filtro de búsqueda: nombre del vino o denominación de origen
      if (activeSearch) {
        const searchTerm = activeSearch.toLowerCase();
        const wineName = wine.name?.toLowerCase() || '';
        const categoryName = getCategoryName(wine.categoryId)?.toLowerCase() || '';
        if (!wineName.includes(searchTerm) && !categoryName.includes(searchTerm)) {
          return false;
        }
      }
      // Filtro de categoría
      if (filters.categoryId && wine.categoryId !== filters.categoryId) {
        return false;
      }
      // Filtro de bodega
      if (filters.wineryId && wine.wineryId !== filters.wineryId) {
        return false;
      }
      // Filtro de tipo
      if (filters.type && wine.type !== filters.type) {
        return false;
      }
      return true;
    }).sort((a, b) => {
      const sortBy = filters.sortBy || 'name';
      const sortOrder = filters.sortOrder || 'asc';
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [wines, activeSearch, filters]);

  // Calcular tipos de vino disponibles según los vinos filtrados actualmente
  const filteredTypes = useMemo(() => {
    if (!Array.isArray(filteredWines)) return [];
    const typesSet = new Set();
    filteredWines.forEach(wine => {
      if (wine.type) typesSet.add(wine.type);
    });
    return Array.from(typesSet);
  }, [filteredWines]);

  // Agrupar vinos filtrados por categoría
  const winesByCategory = filteredWines.length > 0 ? filteredWines.reduce((acc, wine) => {
    const categoryName = getCategoryName(wine.categoryId);
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(wine);
    return acc;
  }, {}) : {};

  // Mapear vinos por ID para acceso rápido
  const winesById = useMemo(() => {
    const map = {};
    wines.forEach(w => { map[w.id] = w; });
    return map;
  }, [wines]);

  // Secciones personalizadas activas con vinos asignados
  const activeSections = Array.isArray(sectionsData) ? sectionsData.filter(s => s.isActive && Array.isArray(s.wineIds) && !s.isCategory) : [];

  // Detectar si hay búsqueda activa
  const isSearching = activeSearch && activeSearch.trim().length > 0;

  // Renderizado de secciones personalizadas
  const renderCustomSections = () => (
    <>
      {visibleSections
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((section, idx, arr) => {
          const sectionWines = section.wineIds.map(id => winesById[id]).filter(Boolean);
          if (sectionWines.length === 0) return null;
          return (
            <React.Fragment key={section.id}>
              <Box sx={{ mb: 6 }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 3, background: 'white', border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                    <TrendingIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                    <Typography variant="h4" sx={{ fontFamily: 'Marzo', fontWeight: 600, color: 'secondary.main' }}>
                      {section.name}
                    </Typography>
                    <Chip label={`${sectionWines.length} vinos`} sx={{ ml: 2, backgroundColor: '#722F37', color: 'white' }} />
                  </Box>
                  <ProductCarousel
                    title={section.name}
                    products={sectionWines}
                    onAddToCart={handleAddToCart}
                    imageErrors={imageErrors}
                    handleImageError={handleImageError}
                    formatPrice={formatPrice}
                    setQuickViewWine={setQuickViewWine}
                    setQuickViewOpen={setQuickViewOpen}
                  />
                </Paper>
              </Box>
              {/* Separador entre secciones personalizadas, excepto después de la última */}
              {idx < arr.length - 1 && <SectionDivider />}
            </React.Fragment>
          );
        })}
    </>
  );

  // Calcular total de items en el carrito
  const cartItemsCount = cart.reduce((total, item) => total + item.quantity, 0);

  const handleAddToCart = useCallback((wine) => {
    addToCart({
      ...wine,
      categoryName: getCategoryName(wine.categoryId)
    });
    setAlert({
      type: 'success',
      message: `${wine.name} agregado al carrito`
    });
    setTimeout(() => setAlert(null), 3000);
  }, [addToCart]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Función para manejar errores de imagen
  const handleImageError = useCallback((wineId) => {
    setImageErrors(prev => new Set([...prev, wineId]));
  }, []);

  const shuffleArray = (array) => {
    // Algoritmo de Fisher-Yates
    const arr = array.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  if (winesLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Cargando vinos...
        </Typography>
      </Container>
    );
  }

  // Si no hay vinos, mostrar mensaje
  if (!wines || wines.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No hay vinos disponibles en este momento.
        </Typography>
      </Container>
    );
  }

  const winesByVisibleCategory = filteredWines.length > 0 ? filteredWines.reduce((acc, wine) => {
    const category = visibleCategories.find(cat => cat.id === wine.categoryId);
    if (!category) return acc;
    const categoryName = category.name;
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(wine);
    return acc;
  }, {}) : {};

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Roboto, Arial, sans-serif' }}>
      <Container maxWidth="lg" sx={{ py: 2, position: 'relative', zIndex: 1 }}>
        {/* Alerta */}
        {alert && (
          <Alert 
            severity={alert.type} 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              '& .MuiAlert-icon': {
                color: 'inherit'
              }
            }}
            onClose={() => setAlert(null)}
          >
            {alert.message}
          </Alert>
        )}

        {/* Indicador de datos de ejemplo */}
        {winesError && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            Mostrando catálogo de ejemplo. Para funcionalidad completa, ejecuta el servidor backend.
          </Alert>
        )}

        {/* Hero Section */}
        <HeroSection />

        {/* Filtros */}
        <FilterSection 
          filters={filters} 
          handleFilterChange={handleFilterChange} 
          categories={categories} 
          wineTypes={filteredTypes} 
          wineries={filteredWineries}
          onSearch={handleSearch}
        />

        {/* Estadísticas del catálogo */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 1, md: 3 }, // Aún menos padding en móvil
            mb: 4, 
            borderRadius: 3,
            background: `linear-gradient(rgba(44,24,16,0.65), rgba(114,47,55,0.55)), url('/images/uvas.png') center/cover no-repeat`,
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0
            }
          }}
        >
          <Grid container spacing={{ xs: 0.5, md: 3 }} sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={6} md={4}>
              <Box textAlign="center">
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  mb: { xs: 0.25, md: 1 },
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                  fontSize: { xs: '1.4rem', md: '2.5rem' } // Más pequeño en móvil
                }}>
                  {filteredWines.length}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#f5f5f5',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.6)',
                  fontWeight: 500,
                  fontSize: { xs: '0.78rem', md: '1.1rem' } // Más pequeño en móvil
                }}>
                  Vinos Disponibles
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={4}>
              <Box textAlign="center">
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  mb: { xs: 0.25, md: 1 },
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                  fontSize: { xs: '1.4rem', md: '2.5rem' } // Más pequeño en móvil
                }}>
                  {Object.keys(winesByCategory).length}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#f5f5f5',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.6)',
                  fontWeight: 500,
                  fontSize: { xs: '0.78rem', md: '1.1rem' } // Más pequeño en móvil
                }}>
                  Denominaciones
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: { xs: 'flex', md: 'block' }, justifyContent: { xs: 'center', md: 'initial' } }}>
              <Box textAlign="center">
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  mb: { xs: 0.25, md: 1 },
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                  fontSize: { xs: '1.4rem', md: '2.5rem' } // Más pequeño en móvil
                }}>
                  {filteredTypes.length}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#f5f5f5',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.6)',
                  fontWeight: 500,
                  fontSize: { xs: '0.78rem', md: '1.1rem' } // Más pequeño en móvil
                }}>
                  Tipos de Vino
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Renderizado condicional según búsqueda */}
        {!isSearching && (
          <>
            {/* Secciones personalizadas activas: solo mostrar si no hay categoría ni bodega seleccionada */}
            {(!filters.categoryId && !filters.wineryId) && renderCustomSections()}

            {/* Vinos agrupados por categoría SOLO para categorías visibles */}
            {Object.keys(winesByVisibleCategory)
              .sort((a, b) => a.localeCompare(b))
              .map((category, idx, arr) => (
                <React.Fragment key={category}>
                  <Box sx={{ mb: 6 }}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 4, 
                        borderRadius: 3,
                        background: 'white',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                        <TrendingIcon sx={{ mr: 2, color: 'secondary.main', fontSize: 32 }} />
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontFamily: 'Marzo',
                            fontWeight: 600,
                            color: 'secondary.main'
                          }}
                        >
                          {category}
                        </Typography>
                        <Chip 
                          label={`${winesByVisibleCategory[category].length} vinos`}
                          sx={{ ml: 2, backgroundColor: '#722F37', color: 'white' }}
                        />
                      </Box>
                      
                      <ProductCarousel
                        title={category}
                        products={shuffleArray(winesByVisibleCategory[category])}
                        onAddToCart={handleAddToCart}
                        imageErrors={imageErrors}
                        handleImageError={handleImageError}
                        formatPrice={formatPrice}
                        setQuickViewWine={setQuickViewWine}
                        setQuickViewOpen={setQuickViewOpen}
                      />
                    </Paper>
                  </Box>
                  {/* Separador entre secciones, excepto después de la última */}
                  {idx < arr.length - 1 && <SectionDivider />}
                </React.Fragment>
            ))}
          </>
        )}

        {/* Si hay búsqueda activa, mostrar grid de resultados ignorando visibilidad de sección/categoría */}
        {isSearching && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" sx={{ mb: 3, fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 600, color: 'primary.main' }}>
              Resultados de búsqueda
              <Chip label={`${filteredWines.length} vinos`} sx={{ ml: 2, backgroundColor: '#722F37', color: 'white' }} />
            </Typography>
            <Grid container spacing={4}>
              {filteredWines.map((wine) => (
                <Grid item xs={12} sm={6} md={4} key={wine.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, boxShadow: '0 2px 8px rgba(114,47,55,0.07)' }}>
                    {wine.imageUrl && !imageErrors.has(wine.id) ? (
                      <CardMedia
                        component="img"
                        height="280"
                        image={wine.imageUrl}
                        alt={wine.name}
                        onError={() => handleImageError(wine.id)}
                        sx={{ objectFit: 'contain', background: '#f8f9fa', cursor: 'pointer' }}
                        onClick={() => {
                          setQuickViewWine(wine);
                          setQuickViewOpen(true);
                        }}
                      />
                    ) : (
                      <WinePlaceholder name={wine.name} />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 700 }}>
                        {wine.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Chip label={wine.type} size="small" sx={{ backgroundColor: '#722F37', color: 'white' }} />
                        <Chip label={wine.year} size="small" sx={{ backgroundColor: '#e0e0e0', color: '#722F37' }} />
                        <Chip label={`${wine.alcoholContent}% vol.`} size="small" sx={{ backgroundColor: '#fff7e6', color: '#722F37', border: '1px solid #e0e0e0' }} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {wine.description}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                        {formatPrice(wine.price)}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ backgroundColor: '#e5d1c0', color: '#722F37', fontWeight: 700, borderRadius: 2, '&:hover': { backgroundColor: '#f5e6d8' } }}
                        onClick={() => handleAddToCart(wine)}
                      >
                        + Añadir al Carrito
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {filteredWines.length === 0 && !winesLoading && (
          <Paper 
            elevation={0}
            sx={{ 
              textAlign: 'center', 
              py: 8,
              borderRadius: 3,
              background: 'white',
              border: '1px solid #e0e0e0'
            }}
          >
            <WineIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No se encontraron vinos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Intenta ajustar los filtros de búsqueda
            </Typography>
          </Paper>
        )}

        {/* Botón flotante del carrito */}
        {cartItemsCount > 0 && (
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
              width: 64,
              height: 64,
              boxShadow: '0 8px 32px rgba(114, 47, 55, 0.3)',
              backgroundColor: '#722F37',
              '&:hover': {
                backgroundColor: '#8B4513',
                transform: 'scale(1.1)',
                boxShadow: '0 12px 40px rgba(114, 47, 55, 0.4)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onClick={() => navigate('/cart')}
          >
            <Badge 
              badgeContent={cartItemsCount} 
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#DAA520',
                  color: '#FFFFFF',
                  fontWeight: 700
                }
              }}
              max={99}
            >
              <CartIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />
            </Badge>
          </Fab>
        )}
      </Container>
      <QuickViewModal
        open={quickViewOpen}
        wine={quickViewWine}
        onClose={() => setQuickViewOpen(false)}
        onAddToCart={(wine) => {
          handleAddToCart(wine);
          setQuickViewOpen(false);
        }}
        imageErrors={imageErrors}
        handleImageError={handleImageError}
        formatPrice={formatPrice}
      />
    </Box>
  );
};

export default Home; 